import React, { useState } from "react";
import { connect } from "react-redux";
import classnames from "classnames";
import { Confirm, LayerAPI, LayerUtilsAPI, UserAPI } from "@ai360/core";
import { InjectedIntl, injectIntl } from "@ai360/core/intl";
import { getActiveModule } from "~/action-panel";
import { ActionPanelModuleList, setActiveModule } from "~/action-panel/actions";
import {
    ErrorDetailsContextMenuLabel,
    ErrorDetailsDialog,
} from "~/action-panel/components/common/error-details-dialog/error-details-dialog";
import { StatusCodes } from "~/action-panel/components/common/status-messages";
import { setTriggeredPage as setEventTriggeredPage } from "~/action-panel/components/event-module/actions";
import * as eventInfoActions from "~/action-panel/components/event-module/components/event-info/actions";
import { setTriggeredPage as setRecTriggeredPage } from "~/action-panel/components/rec-module/actions";
import * as recInfoActions from "~/action-panel/components/rec-module/components/rec-info/actions";
import { Menu } from "~/core";
import { getUser } from "~/login";
import { eventsSelectors, recsSelectors, eventsModels, recsModels } from "~/recs-events";
import {
    ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET,
    ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL,
    ANALYSIS_INFO_NAME_MANAGEMENT_AREA,
} from "~/recs-events/analysis/model";
import { logFirebaseEvent } from "~/utils/firebase";
import { isEventLayerType } from "../../utils";
import * as analysisActions from "../analysis-info/actions";
import * as layerListActions from "../layer-list/actions";
import { messages } from "../i18n-messages";
import "./layer-item-context-menu.css";
import AnalysisBatchDeleteDialogBox from "../analysis-info/analysis-batch-delete-dialog-modal";
import AnalysisDeleteDialogBox from "../analysis-info/analysis-delete-dialog-modal";
import { DeleteConfDialog } from "~/action-panel/components/context-menus/item-delete-confirmation-dialog";
import { messages as globalMessages } from "~/i18n-messages";
import { messages as eventModuleMessages } from "~/action-panel/components/event-module/components/i18n-messages";
import { messages as recModuleMessages } from "~/action-panel/components/rec-module/components/i18n-messages";

interface ILayerItemContextMenuProps {
    fieldGuid: string;
    onEditProps: () => void;
    layer: LayerAPI.ILayer;
    onDeleteAnalysisLayer: (
        analysisLayerGuid: string,
        fieldGuid: string,
        layerType: string
    ) => void;
    type: number;
    activeModule: string;
    onShowAnalysisLayer: (analysisInfoGuid: string) => void;
    onShowEventInfo: (eventSummary: eventsModels.AgEventSummary) => void;
    onShowRecInfo: (recSummary: recsModels.RecSummary) => void;
    onDeleteLayerEvent: (agEventGeneralGuid: string, fieldGuid: string) => void;
    onDeleteLayerRec: (recGeneralGuid: string, fieldGuid: string) => void;
    eventSummary: eventsModels.AgEventSummary | undefined;
    recSummary: recsModels.RecSummary | undefined;
    userInfo: UserAPI.IUser;
    intl: InjectedIntl;
}

const LayerItemContextMenu = (props: ILayerItemContextMenuProps): JSX.Element => {
    //state variables
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
    const [analysisLayerGuid, setAnalysisLayerGuid] = useState<string>("");
    const [confirmationType, setConfirmationType] = useState<Confirm>(null);
    const [errorDetailGuid, setErrorDetailGuid] = useState<string | null>(null);
    const [pendingRecEventDelete, setPendingRecEventDelete] = useState<"event" | "rec" | null>(
        null
    );
    const [isContextMenuExpanded, setIsContextMenuExpanded] = useState<boolean>(false);

    const onDeleteSingle = () => {
        const { fieldGuid, layer } = props;
        if (confirmationType) {
            if (analysisLayerGuid) {
                props.onDeleteAnalysisLayer(analysisLayerGuid, fieldGuid, layer.layerType);
            }
        }
        closeConfirmDialog();
    };

    const closeConfirmDialog = () => {
        setAnalysisLayerGuid("");
        setConfirmationType(Confirm.NONE);
        setIsConfirmationModalOpen(false);
    };

    const getImagerySetupItems = () => {
        const { layer, onShowAnalysisLayer } = props;
        const { formatMessage } = props.intl;
        return [
            {
                label: formatMessage(messages.editImagerySetup),
                action: () => onShowAnalysisLayer(layer.analysisLayerGuid),
            },
            {
                label: formatMessage(messages.deleteImagerySetup),
                action: () => openConfirmDialog(Confirm.DELETE, layer.analysisLayerGuid),
            },
        ];
    };

    const getManagementAreaErrorItems = () => {
        const { layer } = props;
        const { formatMessage } = props.intl;
        return [
            {
                label: formatMessage(ErrorDetailsContextMenuLabel),
                action: () => setErrorDetailGuid(layer.analysisLayerGuid),
            },
            {
                label: formatMessage(messages.deleteAnalysisLayer),
                action: () => openConfirmDialog(Confirm.DELETE, layer.analysisLayerGuid),
            },
        ];
    };

    const getEventItems = () => {
        const { eventSummary, onShowEventInfo } = props;
        const { formatMessage } = props.intl;
        return [
            {
                id: "layer-item-view-edit-event",
                label: formatMessage(messages.viewEditDetails),
                action: () => {
                    logFirebaseEvent("layers_view_edit_event");
                    onShowEventInfo(eventSummary);
                },
                disabled: eventSummary.importedStatus === StatusCodes.ErrorCreatingEvent,
            },
            {
                id: "layer-item-delete-event",
                label: formatMessage(messages.deleteEvent),
                action: () => setPendingRecEventDelete("event"),
            },
        ];
    };

    const getRecItems = () => {
        const { recSummary, onShowRecInfo } = props;
        const { formatMessage } = props.intl;
        return [
            {
                id: "layer-item-view-edit-rec",
                label: formatMessage(messages.viewEditDetails),
                action: () => {
                    logFirebaseEvent("layers_view_edit_rec");
                    onShowRecInfo(recSummary);
                },
            },
            {
                id: "layer-item-delete-rec",
                label: formatMessage(messages.deleteRec),
                action: () => setPendingRecEventDelete("rec"),
            },
        ];
    };

    const getMenuItems = () => {
        const { eventSummary, layer, recSummary, userInfo } = props;
        const editMenuItems = [];
        const layerType = LayerUtilsAPI.getLayerType(layer);

        if (userInfo.role.layerAnalysis && isAnalysisLayer(layer)) {
            if (userInfo.role.analysisLayers) {
                if (
                    layer.layerType === ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET ||
                    layer.layerType === ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL
                ) {
                    editMenuItems.push(...getImagerySetupItems());
                } else if (layer.layerType === ANALYSIS_INFO_NAME_MANAGEMENT_AREA) {
                    editMenuItems.push(...getManagementAreaErrorItems());
                }
            }
        } else if (isEventLayerType(layerType)) {
            if (
                eventSummary &&
                eventsModels.canEditDeleteEvent(eventSummary.agEventTypeName, userInfo.role)
            ) {
                editMenuItems.push(...getEventItems());
            }
        } else if (layerType === LayerUtilsAPI.LayerType.REC) {
            if (recSummary && recsModels.canEditDeleteRec(recSummary.recType, userInfo.role)) {
                editMenuItems.push(...getRecItems());
            }
        }

        return editMenuItems.map((menuItem, key) => {
            return { ...menuItem, key };
        });
    };

    const openConfirmDialog = (confirmationType, analysisLayerGuid) => {
        setAnalysisLayerGuid(analysisLayerGuid ? analysisLayerGuid : null);
        setConfirmationType(confirmationType);
        setIsConfirmationModalOpen(true);
    };

    const isBatched = () => {
        const { layer } = props;
        const surfaceInfo = layer?.subLayers?.[0];
        return (
            surfaceInfo &&
            isAnalysisLayer(layer) &&
            surfaceInfo.analysisInBatch &&
            surfaceInfo.analysisInBatch.length > 1
        );
    };

    const isAnalysisLayer = (layer: LayerAPI.ILayer) => {
        return layer.analysisLayerGuid && layer.analysisLayerGuid.length > 0;
    };

    const deleteDialogBox = () => {
        const { layer } = props;
        const surfaceInfo = layer?.subLayers?.[0];
        const useBatchDialogue = isBatched();
        if (surfaceInfo && useBatchDialogue) {
            return (
                <AnalysisBatchDeleteDialogBox
                    closeConfirmDialog={() => closeConfirmDialog()}
                    isConfirmationModalOpen={isConfirmationModalOpen}
                    onDeleteSingle={() => onDeleteSingle()}
                    surfaceInfo={surfaceInfo}
                ></AnalysisBatchDeleteDialogBox>
            );
        }
        return (
            <AnalysisDeleteDialogBox
                closeConfirmDialog={() => closeConfirmDialog()}
                isConfirmationModalOpen={isConfirmationModalOpen}
                onDeleteSingle={() => onDeleteSingle()}
            ></AnalysisDeleteDialogBox>
        );
    };

    const errorDetailsDialog = errorDetailGuid && (
        <ErrorDetailsDialog
            onClose={() => setErrorDetailGuid(null)}
            correlationId={errorDetailGuid}
            logType="createanalysis"
        />
    );

    const recEventDeleteDialog = !pendingRecEventDelete ? null : (
        <DeleteConfDialog
            deleteConfirmationTitle={globalMessages.confirmTitle}
            deleteConfirmationMessage={
                pendingRecEventDelete === "event"
                    ? eventModuleMessages.deleteEventConfirmation
                    : recModuleMessages.deleteRecConfirmation
            }
            onConfirmationClick={(confirmation) => {
                const kind = pendingRecEventDelete;
                setPendingRecEventDelete(null);
                if (!confirmation) {
                    return;
                }
                if (kind === "event") {
                    props.onDeleteLayerEvent(props.layer.agEventGeneralGuid, props.fieldGuid);
                } else {
                    props.onDeleteLayerRec(props.layer.recGeneralGuid, props.fieldGuid);
                }
            }}
        />
    );

    const { layer } = props;
    const menuItems = getMenuItems();
    if (menuItems.length === 0) {
        return null;
    }

    return (
        <div className="context-menu-container">
            <Menu
                className={classnames("context-menu layer-context-menu", {
                    expanded: isContextMenuExpanded,
                })}
                isDotMenu={true}
                getMenuItems={() => menuItems}
                onExpand={() => setIsContextMenuExpanded(true)}
                onCollapse={() => setIsContextMenuExpanded(false)}
            />
            {isAnalysisLayer(layer) && deleteDialogBox()}
            {errorDetailsDialog}
            {recEventDeleteDialog}
        </div>
    );
};

const mapStateToProps = (state, props: Partial<ILayerItemContextMenuProps>) => ({
    ...props,
    activeModule: getActiveModule(state),
    eventSummary: !props.layer?.agEventGeneralGuid
        ? undefined
        : eventsSelectors
              .getEventGeneralGuidToEventSummaryMap(state)
              .get(props.layer.agEventGeneralGuid),
    recSummary: !props.layer?.recGeneralGuid
        ? undefined
        : recsSelectors.getRecGeneralGuidToRecSummaryMap(state).get(props.layer.recGeneralGuid),
    userInfo: getUser(state),
});

const mapDispatchToProps = (dispatch) => ({
    onShowAnalysisLayer: (analysisInfoGuid) =>
        dispatch(analysisActions.showAnalysisInfo(analysisInfoGuid)),
    onDeleteAnalysisLayer: (analysisLayerGuid, fieldGuid, layerType) =>
        dispatch(analysisActions.deleteAnalysisLayer(analysisLayerGuid, fieldGuid, layerType)),
    onShowEventInfo: (eventSummary) => {
        dispatch(setEventTriggeredPage(ActionPanelModuleList.LAYER));
        dispatch(setActiveModule(ActionPanelModuleList.EVENT));
        dispatch(eventInfoActions.showEventInfo(eventSummary));
    },
    onShowRecInfo: (recSummary) => {
        dispatch(setRecTriggeredPage(ActionPanelModuleList.LAYER));
        dispatch(setActiveModule(ActionPanelModuleList.REC));
        dispatch(recInfoActions.showRecInfo(recSummary));
    },
    onDeleteLayerEvent: (agEventGeneralGuid, fieldGuid) =>
        dispatch(layerListActions.deleteLayerEvent(agEventGeneralGuid, fieldGuid)),
    onDeleteLayerRec: (recGeneralGuid, fieldGuid) =>
        dispatch(layerListActions.deleteLayerRec(recGeneralGuid, fieldGuid)),
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(LayerItemContextMenu));
